import { AsyncResult, Atom, Reactivity } from 'effect/reactivity';
import { Duration, Effect } from 'effect';
import {
  apiMutation,
  apiQuery,
  ssrApiQuery,
  withOptimisticUpdate,
} from './api-runtime';
import {
  type CheckoutConfigRequest,
  dismissAnnouncementEffect,
  getBillingStatusEffect,
  getCheckoutConfigEffect,
  getDismissedAnnouncementsEffect,
  getPaymentDetailsEffect,
  getPaymentSubscriptionsEffect,
  getReferralListEffect,
  getUserPreferencesEffect,
  getUserProfileEffect,
  getUserSummaryEffect,
  setReferredByCodeEffect,
  type UpdateUserProfilePayload,
  type UserPreferencesUpdates,
  updateUserPreferencesEffect,
  updateUserProfileEffect,
} from './user';
import {
  type AnnouncementId,
  SummaryResponse,
  UserProfileData,
} from '@listening/api-contract';
import type { ReactivityKey } from './reactivity-keys';

// The current viewer's account data, shared by start, mobile and the browser
// extension. Whether a read runs at all is the app's call: an app that is not
// signed in reads `idleQuery` instead.

const staleTime = Duration.hours(1);

/** Reactivity keys for the viewer's data. A write that changes one of these
 * on the server invalidates it. */
export const userKeys = {
  summary: 'user:summary',
  profile: 'user:profile',
  preferences: 'user:preferences',
  billing: 'user:billing',
  subscriptions: 'user:subscriptions',
  paymentDetails: 'user:payment-details',
  referrals: 'user:referrals',
  announcements: 'user:announcements',
} as const satisfies Record<string, ReactivityKey>;

/** The viewer's summary. A page the server renders sends it along. */
export const userSummaryAtom = ssrApiQuery(getUserSummaryEffect, {
  key: 'user-summary',
  success: SummaryResponse,
  staleTime,
  reactivityKeys: [userKeys.summary],
});

/** The viewer's name. A page the server renders sends it along. */
export const userProfileAtom = ssrApiQuery(getUserProfileEffect, {
  key: 'user-profile',
  success: UserProfileData,
  staleTime,
  reactivityKeys: [userKeys.profile],
});

export const userPreferencesAtom = apiQuery(getUserPreferencesEffect, {
  staleTime,
  reactivityKeys: [userKeys.preferences],
});

/** The voice a signed-out listener chose on this page; it is never saved. */
export const guestVoiceAtom = Atom.make<string | undefined>(undefined).pipe(
  Atom.keepAlive,
);

export const paymentSubscriptionsAtom = apiQuery(
  getPaymentSubscriptionsEffect,
  { staleTime, reactivityKeys: [userKeys.subscriptions, userKeys.billing] },
);

export const paymentDetailsAtom = apiQuery(getPaymentDetailsEffect, {
  staleTime,
  reactivityKeys: [userKeys.paymentDetails, userKeys.billing],
});

export const referralListAtom = apiQuery(getReferralListEffect, {
  staleTime,
  reactivityKeys: [userKeys.referrals],
});

/**
 * `GET /billing/status` — entitlement and billing relationship, server-derived.
 *
 * Its own read rather than a slice of the summary: the two answer different
 * questions and are invalidated by different writes, and mobile's frozen
 * `/user/summary.metering` contract — the field names shipped mobile binaries
 * gate on — must not acquire a second consumer that expects it to move in step
 * with billing.
 */
export const billingStatusAtom = apiQuery(getBillingStatusEffect, {
  staleTime,
  reactivityKeys: [userKeys.billing],
});

/**
 * `GET /billing/checkout-config`. Deliberately *not* an hour stale on the
 * paywall: the offer carries an experiment assignment and a promo evaluation,
 * and a purchase surface rendering an hour-old offer is how a user is shown one
 * price and charged another. The server re-reads both when the subscription is
 * created, so a stale render cannot buy the wrong thing — it can only confuse.
 * The plan page buys nothing from this offer, since a plan change confirms
 * against its own server preview, so it keeps the hour; so does the `offer`
 * preview, whose buy button leads to the paywall's own fresh read.
 */
export const checkoutConfigAtom = Atom.family(
  (request: CheckoutConfigRequest) =>
    apiQuery(getCheckoutConfigEffect(request), {
      staleTime: request.surface === 'paywall' ? Duration.zero : staleTime,
      reactivityKeys: [userKeys.billing],
    }),
);

export const dismissedAnnouncementsAtom = apiQuery(
  getDismissedAnnouncementsEffect,
  { staleTime: Duration.minutes(5), reactivityKeys: [userKeys.announcements] },
);

export const dismissAnnouncementAtom = apiMutation(
  (announcement: AnnouncementId) => dismissAnnouncementEffect(announcement),
  { reactivityKeys: [userKeys.announcements] },
);

/** Saves preferences and shows them at once, putting them back if the save
 * fails; the refetch reconciles. */
export const updateUserPreferencesAtom = apiMutation(
  (updates: UserPreferencesUpdates) =>
    withOptimisticUpdate(
      userPreferencesAtom,
      (preferences) => ({
        voice: updates.voice ?? preferences.voice,
        most_recent_language:
          updates.most_recent_language ?? preferences.most_recent_language,
      }),
      updateUserPreferencesEffect(updates),
    ),
  { reactivityKeys: [userKeys.preferences] },
);

/**
 * Saves profile changes and shows the saved profile at once. `afterSave`
 * refreshes whatever else carries the profile (mobile re-reads the auth
 * session for the name); only when it succeeds is the profile refetched, so a
 * failed refresh keeps the saved values on screen rather than a stale read.
 */
export const patchUserProfileEffect = <E, R>(
  changes: UpdateUserProfilePayload,
  afterSave: Effect.Effect<unknown, E, R> = Effect.void,
) =>
  updateUserProfileEffect(changes).pipe(
    Effect.tap((profile) =>
      Atom.update(userProfileAtom, (current) => ({
        ...AsyncResult.getOrElse(current, () => ({})),
        ...profile,
      })),
    ),
    Effect.tap(() =>
      afterSave.pipe(
        Effect.andThen(Reactivity.invalidate([userKeys.profile])),
        Effect.ignore,
      ),
    ),
  );

export const patchUserProfileAtom = apiMutation(
  (changes: UpdateUserProfilePayload) => patchUserProfileEffect(changes),
);

export const setReferredByCodeAtom = apiMutation(
  (referralCode: string) => setReferredByCodeEffect(referralCode),
  { reactivityKeys: [userKeys.summary, userKeys.referrals] },
);
